// گزارش کلی اسناد هزینه
// props: { user, onClose | onBack, programKind (0|1), year, onOpenDocument?(hazinehDocumentId) }
import React, { useState, useEffect } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import { openReport } from '../../components/report';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);
const fmtAmt = n => (Number(n || 0) ? Number(n).toLocaleString('fa-IR') : '•');

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const itemsOf = (lv) => [
  ['FinancialYear', 'سال مالی'], ['Department', 'واحد اجرایی'], ['DocumentSummary', 'نماینده اسناد'],
  ['HazinehDocument', 'سند هزینه'], ['HazinehDocumentDetail', 'ریز سند هزینه'], ['PayKind', 'محل پرداخت'],
  ['PaymentMethod', 'نحوه پرداخت'], ['Buyer', 'کارپرداز'], ['Credit', 'منبع اعتبار'], ['Program', 'برنامه/طرح'],
  ['Project', 'پروژه/فعالیت'], ['Hazineh', lv[1]], ['Hazineh1', lv[2]], ['Hazineh2', lv[3]], ['Hazineh3', lv[4]],
  ['Hazineh4', lv[5]], ['Hazineh5', lv[6]], ['Havaleh', 'حواله'], ['AmountReceiver', 'فروشنده'], ['Factor', 'فاکتور'],
  ['Evaluation', 'نوع سند'],
];
const MODES = [[0, '—'], [3, 'سطر گزارش'], [1, 'سرجمع گزارش'], [2, 'سرجمع میانی']];
const REPORTS = [[0, 'تنخواه دریافتی و پرداختها'], [1, 'وضعیت اسناد هزینه']];
const SCALES = ['ریال', 'هزار ریال', 'میلیون ریال'];
const DETAIL_COLS = [
  { key: 'document_no', label: 'سند' }, { key: 'document_date', label: 'تاریخ' }, { key: 'remark', label: 'شرح سند' },
  { key: 'detail_remark', label: 'شرح ریز' }, { key: 'amount', label: 'مبلغ', num: true },
];

function Table({ cols, rows, onRow, onDouble, onContext }) {
  return (
    <table style={ui.table}>
      <thead><tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length || 1} style={ui.empty}>موردی یافت نشد</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} onClick={() => onRow && onRow(r)} onDoubleClick={() => onDouble && onDouble(r)}
            onContextMenu={e => { if (onContext) { e.preventDefault(); onContext(r); } }}
            style={{ background: i % 2 ? '#fdf6ec' : '#fff', cursor: 'pointer' }}>
            {cols.map(c => (
              <td key={c.key} style={c.num ? ui.tdNum : { ...ui.td, whiteSpace: 'pre-wrap', textAlign: 'right' }}>
                {c.num ? fmtAmt(r[c.key]) : toFa(r[c.key])}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SelectionList({ label, list, onChange, onClose }) {
  const [q, setQ] = useState('');
  const shown = list.filter(o => !q || String(o.title ?? '').includes(q) || String(o.code ?? '').includes(q));
  const setAll = (fn) => onChange(list.map(o => (shown.includes(o) ? { ...o, sel: fn(o) } : o)));
  const count = list.filter(o => o.sel).length;
  return (
    <div style={ui.overlay}>
      <div style={{ ...ui.modal, maxWidth: '560px', height: '80vh' }}>
        <div style={ui.mHeader}><span style={ui.mTitle}>{label}</span>
          <button style={ui.closeBtn} onClick={onClose}>✕</button></div>
        <div style={{ ...ui.mBody, display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input style={ui.input} value={q} onChange={e => setQ(e.target.value)} placeholder="جستجو…" />
            <button style={ui.btnCancel} onClick={() => setAll(() => 1)}>همه</button>
            <button style={ui.btnCancel} onClick={() => setAll(() => 0)}>هیچ</button>
            <button style={ui.btnCancel} onClick={() => setAll(o => (o.sel ? 0 : 1))}>معکوس</button>
          </div>
          <div style={{ fontSize: '12px', color: '#555' }}>{toFa(count)} از {toFa(list.length)} انتخاب شده</div>
          <div style={{ flex: 1, overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
            {shown.map(o => (
              <label key={String(o.id)} style={s.optRow}>
                <input type="checkbox" checked={!!o.sel}
                  onChange={() => onChange(list.map(x => (x === o ? { ...x, sel: x.sel ? 0 : 1 } : x)))} />
                <span style={{ minWidth: '90px', color: '#1a2f50' }}>{toFa(o.code)}</span><span>{toFa(o.title)}</span>
              </label>
            ))}
          </div>
        </div>
        <div style={ui.mFooter}><button style={ui.btnSave} onClick={onClose}>تایید</button></div>
      </div>
    </div>
  );
}

export default function HazinehDocumentAllReports({ user, onClose, onBack, programKind = 0, year: initYear = '', onOpenDocument }) {
  const kind = Number(programKind) === 1 ? 1 : 0;
  const back = onClose || onBack;
  const title = kind === 0 ? 'گزارش کلی اسناد هزینه اعتبارات هزینه ای' : 'گزارش کلی اسناد هزینه اعتبارات تملک دارایی های سرمایه ای';
  const [year, setYear] = useState(initYear);
  const [levels, setLevels] = useState(['', 'فصل', 'زیرفصل', 'قسمت', 'قلم', 'کدهزینه سطح چهارم', 'کدهزینه سطح پنجم']);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [report, setReport] = useState(0);
  const [scale, setScale] = useState(0);
  const [modes, setModes] = useState({});
  const [opts, setOpts] = useState({});
  const [picking, setPicking] = useState(null);
  const [showSel, setShowSel] = useState(true);
  const [result, setResult] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [saved, setSaved] = useState(null);
  const [savedId, setSavedId] = useState(0);
  const [repTitle, setRepTitle] = useState('');
  const [repRemark, setRepRemark] = useState('');
  const [askDel, setAskDel] = useState(false);

  const items = itemsOf(levels);
  const labelOf = (n) => (items.find(i => i[0] === n) || [n, n])[1];

  const loadOptions = (name, y = year) => API(`/all-reports/options/${name}/?year=${y || ''}`).then(j => {
    if (j.ok) setOpts(o => ({ ...o, [name]: j.data }));
    return j;
  });

  useEffect(() => {
    API(`/all-reports/init/?year=${initYear || ''}`).then(j => {
      if (!j.ok) { setError(j.msg); return; }
      setYear(j.year); setLevels(j.levels); setFromDate(j.from_date); setToDate(j.to_date);
      loadOptions('FinancialYear', j.year);
    });
  }, []);

  const state = () => {
    const selections = {};
    Object.entries(opts).forEach(([n, list]) => {
      selections[n] = { sel: list.filter(o => o.sel).map(o => o.id), unsel: list.filter(o => !o.sel).map(o => o.id) };
    });
    const labels = {};
    items.forEach(([n, l]) => { labels[n] = l; });
    return { program_kind: kind, year, report, scale, from_date: fromDate, to_date: toDate, modes, selections, labels, title: repTitle };
  };

  const openPick = (name) => {
    if (!opts[name]) {
      loadOptions(name).then(j => {
        if (j.ok && j.data.length === 0) setMsg(['هیچ عنوانی تعریف نشده است.']);
        else if (j.ok) setPicking(name);
      });
    } else setPicking(name);
  };
  const closePick = () => {
    if ((opts[picking] || []).filter(o => o.sel).length === 0) {
      setMsg([`حداقل یکی از ${labelOf(picking)} ها باید انتخاب شود`]); return;
    }
    setPicking(null);
  };

  const prepare = async () => {
    setLoading(true); setError('');
    const j = await API('/all-reports/prepare/', { method: 'POST', body: JSON.stringify(state()) });
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setResult({ columns: j.columns, rows: j.data }); setShowSel(false);
  };

  const print = () => {
    if (!result.rows.length) return;
    openReport('tadarokat.hazineh_all_reports', { state: JSON.stringify(state()) }).catch(e => setError(e.message));
  };

  const openDetail = async (r) => {
    const keys = {};
    Object.keys(r).forEach(k => { if (k.startsWith('k_')) keys[k.slice(2)] = r[k]; });
    const j = await API('/all-reports/detail/', { method: 'POST', body: JSON.stringify({ state: state(), keys }) });
    if (!j.ok) { setError(j.msg); return; }
    if (j.data.length) setDetail(j.data);
  };

  // انتخاب‌های ذخیره‌شده
  const listSaved = () => API('/all-reports/saved/').then(j => {
    if (!j.ok) { setError(j.msg); return; }
    if (!j.data.length) { setMsg(['هیچ عنوانی تعریف نشده است.']); return; }
    setSaved(j.data);
  });
  const loadSaved = async (r) => {
    setSaved(null);
    const j = await API(`/all-reports/saved/${r.id}/`);
    if (!j.ok) { setError(j.msg); return; }
    setSavedId(r.id); setRepTitle(r.title || ''); setRepRemark(r.remark || '');
    setReport(j.report || 0); setModes(j.modes || {});
    const next = {};
    for (const n of Object.keys(opts)) next[n] = opts[n].map(o => ({ ...o, sel: 1 }));
    for (const n of Object.keys(j.excluded || {})) {
      const lj = next[n] ? { ok: true, data: next[n] } : await API(`/all-reports/options/${n}/?year=${year}`);
      if (lj.ok) next[n] = lj.data.map(o => ({ ...o, sel: (j.excluded[n] || []).includes(Number(o.id)) ? 0 : 1 }));
    }
    setOpts(next);
  };
  const saveSelection = async (asNew) => {
    if (!repTitle.trim()) { setMsg(['عنوان گزارش مشخص نشده است']); return; }
    if (!asNew && !savedId) { setMsg(['گزارشی برای ویرایش انتخاب نشده است']); return; }
    const excluded = {};
    Object.entries(opts).forEach(([n, list]) => { excluded[n] = list.filter(o => !o.sel).map(o => o.id); });
    const body = JSON.stringify({ title: repTitle, remark: repRemark, report, modes, excluded });
    const j = asNew ? await API('/all-reports/saved/create/', { method: 'POST', body })
      : await API(`/all-reports/saved/${savedId}/`, { method: 'PUT', body });
    if (!j.ok) { setError(j.msg); return; }
    setSavedId(j.id); setMsg(['ذخیره شد']);
  };
  const deleteSaved = async () => {
    setAskDel(false);
    const j = await API(`/all-reports/saved/${savedId}/`, { method: 'DELETE' });
    if (!j.ok) { setError(j.msg); return; }
    setSavedId(0); setMsg(['حذف شد']);
  };

  // ستون‌های خالی پنهان؛ «مانده» بدون «تنخواه/حواله» معنا ندارد
  const nonEmpty = (k) => result.rows.some(r => Number(r[k] || 0) !== 0 || (typeof r[k] === 'string' && r[k] !== '' && r[k] !== '0'));
  let cols = result.columns.filter(c => nonEmpty(c.key));
  if (report === 0 && !nonEmpty('recv')) cols = cols.filter(c => c.key !== 'remain');
  if (report === 1 && !nonEmpty('havaleh')) cols = cols.filter(c => c.key !== 'remain_no_havaleh');

  const toolbar = (
    <>
      <div style={s.fcol}><label style={ui.flabel}>از تاریخ</label><JalaliDatePicker value={fromDate} onChange={setFromDate} /></div>
      <div style={s.fcol}><label style={ui.flabel}>تا تاریخ</label><JalaliDatePicker value={toDate} onChange={setToDate} /></div>
      <div style={s.fcol}><label style={ui.flabel}>نوع گزارش</label>
        <select style={ui.input} value={report} onChange={e => setReport(Number(e.target.value))}>
          {REPORTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select></div>
      <div style={s.fcol}><label style={ui.flabel}>مبالغ به</label>
        <select style={ui.input} value={scale} onChange={e => setScale(Number(e.target.value))}>
          {SCALES.map((l, i) => <option key={i} value={i}>{l}</option>)}
        </select></div>
      <div style={{ ...s.fcol, flex: 1, minWidth: '160px' }}><label style={ui.flabel}>عنوان گزارش</label>
        <input style={ui.input} value={repTitle} onChange={e => setRepTitle(e.target.value)} /></div>
      <div style={{ ...s.fcol, flex: 1, minWidth: '160px' }}><label style={ui.flabel}>شرح</label>
        <input style={ui.input} value={repRemark} onChange={e => setRepRemark(e.target.value)} /></div>
      <div style={{ ...ui.toolBtns, flexWrap: 'wrap' }}>
        <button style={{ ...ui.btnItems, cursor: 'pointer' }} onClick={() => setShowSel(v => !v)}>انتخابها</button>
        <button style={ui.btnNew} onClick={prepare} disabled={loading}>{loading ? '⏳' : 'تهیه گزارش'}</button>
        <button style={{ ...ui.btnEdit, cursor: 'pointer', opacity: result.rows.length ? 1 : 0.4 }} onClick={print}>گزارش</button>
        <button style={{ ...ui.btnEdit, cursor: 'pointer' }} onClick={listSaved}>گزارشهای ذخیره شده</button>
        <button style={ui.btnNew} onClick={() => saveSelection(true)}>ذخیره جدید</button>
        <button style={{ ...ui.btnEdit, cursor: 'pointer' }} onClick={() => saveSelection(false)}>ویرایش</button>
        <button style={{ ...ui.btnDel, cursor: 'pointer' }} onClick={() => (savedId ? setAskDel(true) : setMsg(['گزارشی برای حذف انتخاب نشده است']))}>حذف</button>
        <ExcelButton title={repTitle || title} columns={cols.map(c => ({ key: c.key, label: c.label, num: c.num }))} rows={result.rows} />
      </div>
    </>
  );

  return (
    <ListPage user={user} onBack={back} title={title} subtitle="تدارکات — سامانه ارجان" toolbar={toolbar} error={error}>
      <div style={ui.splitWrap}>
        {showSel && (
          <div style={{ ...ui.topPane, flex: '0 0 auto', maxHeight: '45%', padding: '10px' }}>
            <div style={s.selGrid}>
              {items.map(([n, l]) => {
                const list = opts[n];
                const cnt = list ? list.filter(o => o.sel).length : null;
                return (
                  <div key={n} style={s.selItem}>
                    <button style={s.selBtn} onClick={() => openPick(n)}>
                      {l}{list && cnt < list.length ? ` (${toFa(cnt)}/${toFa(list.length)})` : ''}
                    </button>
                    <select style={{ ...ui.input, width: '120px', padding: '4px' }} value={modes[n] || 0}
                      onChange={e => setModes(m => ({ ...m, [n]: Number(e.target.value) }))}>
                      {MODES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div style={{ ...ui.bottomPane, borderTop: showSel ? ui.bottomPane.borderTop : 'none' }}>
          <Table cols={cols} rows={result.rows} onContext={openDetail} />
        </div>
      </div>

      {picking && (
        <SelectionList label={labelOf(picking)} list={opts[picking] || []}
          onChange={list => setOpts(o => ({ ...o, [picking]: list }))} onClose={closePick} />
      )}
      {detail && (
        <div style={ui.overlay} onClick={() => setDetail(null)}>
          <div style={{ ...ui.modal, maxWidth: '900px', height: '80vh' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <span style={ui.mTitle}>ریز اقلام — {toFa(detail.length)} ردیف — جمع {fmtAmt(detail.reduce((a, r) => a + Number(r.amount || 0), 0))}</span>
              <button style={ui.closeBtn} onClick={() => setDetail(null)}>✕</button>
            </div>
            <div style={ui.mBody}>
              <Table cols={DETAIL_COLS} rows={detail}
                onDouble={onOpenDocument ? (r) => { setDetail(null); onOpenDocument(r.HazinehDocumentId); } : null} />
            </div>
          </div>
        </div>
      )}
      {saved && (
        <div style={ui.overlay} onClick={() => setSaved(null)}>
          <div style={{ ...ui.modal, maxWidth: '600px', height: '70vh' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}><span style={ui.mTitle}>گزارشهای ذخیره شده</span>
              <button style={ui.closeBtn} onClick={() => setSaved(null)}>✕</button></div>
            <div style={ui.mBody}>
              <Table cols={[{ key: 'title', label: 'عنوان گزارش' }, { key: 'remark', label: 'شرح' }]} rows={saved} onDouble={loadSaved} />
            </div>
          </div>
        </div>
      )}
      {askDel && (
        <WarningDialog title="هشدار" lines={['آيا از حذف اين گزارش مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر" tone="danger"
          onConfirm={deleteSaved} onCancel={() => setAskDel(false)} />
      )}
      {msg && <WarningDialog title="پیام" lines={msg} confirmLabel="تایید" cancelLabel="بستن" onConfirm={() => setMsg(null)} onCancel={() => setMsg(null)} />}
    </ListPage>
  );
}

const s = {
  fcol: { display: 'flex', flexDirection: 'column', gap: '3px' },
  selGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '8px' },
  selItem: { display: 'flex', gap: '6px', alignItems: 'center' },
  selBtn: { flex: 1, padding: '6px 8px', background: '#fff', border: '1.5px solid #1a2f50', borderRadius: '7px', fontFamily: 'inherit', fontSize: '12px', cursor: 'pointer', color: '#1a2f50', textAlign: 'right' },
  optRow: { display: 'flex', gap: '8px', alignItems: 'center', padding: '5px 10px', borderBottom: '1px solid #f3f3f3', fontSize: '12px', cursor: 'pointer' },
};
